import axios from "axios";
import store from "../../Redux/store";
import { Logout } from "../Auth/Auth";
import { auth, authStateReady } from "../../Config/firebase-config";

const API_PATH = process.env.REACT_APP_API_URL;

export interface ApiResponse<T = unknown> {
  data: T;
  error: string;
}

interface AxiosErrorWithConfig {
  config?: { retry?: boolean; url?: string;[key: string]: unknown };
  response?: { status: number };
  message?: string;
}

const sendDeleteCallToSever = (API_URL: string) => {
  return axios.delete(API_URL);
};

const sendPatchCallToSever = <T = unknown>(params: T, API_URL: string) => {
  return axios.patch(API_URL, params);
};

const sendPostCallToSever = <T = unknown>(
  params: T,
  API_URL: string,
  oposite = false
) => {
  if (oposite) return axios.patch(API_URL, params);
  else return axios.post(API_URL, params);
};

const sendGetCallToSever = (API_URL: string) => {
  return axios.get(API_URL);
};

axios.interceptors.request.use(
  async function (config) {
    const apiPath = API_PATH;
    const url = config.url;
    // Wait for Firebase to finish restoring the persisted session on a hard reload,
    await authStateReady();
    // Always get a fresh token — Firebase SDK caches it and auto-refreshes near expiry.
    // This prevents stale Redux tokens from causing 401s on Mac/Safari.
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : "";
    (config.headers as Record<string, string>) = {
      "content-type": "application/json",
      Authorization: "Bearer " + token,
    };
    if (!(config as { retry?: boolean }).retry) config.url = apiPath + url;
    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    if (response) {
      const parsedData = response.data;
      if (response.status === 200 || response.status === 201) {
        return { ...response, data: parsedData } as any;
      } else {
        const errMsg = (response as any).error?.msg ?? "";
        return { ...response, data: {}, error: errMsg } as any;
      }
    }
    return response;
  },
  async function (error: AxiosErrorWithConfig) {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401) {
      // Only retry once to avoid infinite loops
      if (originalRequest.retry) {
        store.dispatch(Logout() as any);
        return Promise.reject(error);
      }
      try {
        // Ensure the initial session restore has completed before deciding the user is
        // gone — on a hard reload currentUser can still be null here mid-rehydration.
        await authStateReady();
        // Force a fresh token from Firebase (bypasses any cached stale token)
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true); // force-refresh so next request picks it up
          originalRequest.retry = true;
          return axios(originalRequest);
        } else {
          // No user — session is gone, log out
          store.dispatch(Logout() as any);
        }
      } catch (refreshError) {
        console.error("Token refresh error:", refreshError);
        store.dispatch(Logout() as any);
      }
    } else if (error.message && error.message === "Network Error") {
      console.log("network error: ", error);
    }
    // Propagate HTTP/API errors (4xx/5xx) so callers can treat failures correctly.
    // Resolving with `{ data: {} }` made failed requests look successful.
    return Promise.reject(error);
  }
);

export {
  sendPostCallToSever,
  sendGetCallToSever,
  sendDeleteCallToSever,
  sendPatchCallToSever,
};
